import { useState, useEffect } from "react";
import { MoreVertical, Flag, Ban, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { ReportDialog } from "./ReportDialog";
import { ContentType, blockUser, isBlocked, unblockUser } from "@/lib/moderation";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/useAuth";

interface UserModerationMenuProps {
  targetUserId: string;
  /** What is being reported. Defaults to "user". */
  contentType?: ContentType;
  contentId?: string | null;
  /** Show as compact icon. Defaults true. */
  compact?: boolean;
  /** Hide block option (e.g. on content reports). */
  hideBlock?: boolean;
  contextLabel?: string;
  onBlocked?: () => void;
}

export const UserModerationMenu = ({
  targetUserId,
  contentType = "user",
  contentId,
  compact = true,
  hideBlock = false,
  contextLabel,
  onBlocked,
}: UserModerationMenuProps) => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [reportOpen, setReportOpen] = useState(false);
  const [blocked, setBlocked] = useState(false);

  useEffect(() => {
    if (!user || !targetUserId || hideBlock) return;
    void isBlocked(targetUserId).then(setBlocked);
  }, [user, targetUserId, hideBlock]);

  if (!user || user.id === targetUserId) return null;

  const handleBlock = async () => {
    try {
      await blockUser(targetUserId);
      setBlocked(true);
      toast({ title: "User blocked", description: "They can no longer message you and you won't see their content." });
      onBlocked?.();
    } catch (err: any) {
      toast({ title: "Couldn't block", description: err.message, variant: "destructive" });
    }
  };

  const handleUnblock = async () => {
    try {
      await unblockUser(targetUserId);
      setBlocked(false);
      toast({ title: "User unblocked" });
    } catch (err: any) {
      toast({ title: "Couldn't unblock", description: err.message, variant: "destructive" });
    }
  };

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size={compact ? "icon" : "sm"}
            className={compact ? "h-8 w-8 text-muted-foreground hover:text-foreground" : ""}
            onClick={(e) => e.stopPropagation()}
            aria-label="More options"
          >
            <MoreVertical className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
          <DropdownMenuItem onClick={() => setReportOpen(true)}>
            <Flag className="h-4 w-4 mr-2" />Report
          </DropdownMenuItem>
          {!hideBlock && (
            <>
              <DropdownMenuSeparator />
              {blocked ? (
                <DropdownMenuItem onClick={handleUnblock}>
                  <ShieldCheck className="h-4 w-4 mr-2" />Unblock user
                </DropdownMenuItem>
              ) : (
                <DropdownMenuItem onClick={handleBlock} className="text-destructive focus:text-destructive">
                  <Ban className="h-4 w-4 mr-2" />Block user
                </DropdownMenuItem>
              )}
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      <ReportDialog
        open={reportOpen}
        onOpenChange={setReportOpen}
        contentType={contentType}
        contentId={contentId}
        reportedUserId={targetUserId}
        contextLabel={contextLabel}
      />
    </>
  );
};
